import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Loader2, Trophy } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import BadgeCard from "@/components/BadgeCard";

const CATEGORY_ORDER = ["démarrage", "régularité", "mémoire", "production", "expertise", "curiosité"];

const CATEGORY_LABELS = {
    "démarrage": "Premiers pas",
    "régularité": "Régularité",
    "mémoire": "Mémoire long terme",
    "production": "Production",
    "expertise": "Expertise",
    "curiosité": "Curiosité",
};

export default function SuccessTab() {
    const [badges, setBadges] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get("/badges");
                setBadges(data.badges || []);
            } catch (err) {
                toast.error(formatApiError(err));
            } finally {
                setLoading(false);
            }
        })();
    }, []);

    const grouped = useMemo(() => {
        const map = {};
        for (const b of badges) {
            const c = b.category || "démarrage";
            if (!map[c]) map[c] = [];
            map[c].push(b);
        }
        return map;
    }, [badges]);

    const totals = useMemo(() => {
        const total = badges.length;
        const earned = badges.filter((b) => b.earned).length;
        return { total, earned };
    }, [badges]);

    if (loading) {
        return (
            <div className="flex items-center justify-center py-12" data-testid="success-loading">
                <Loader2 className="w-5 h-5 animate-spin text-terracotta" />
            </div>
        );
    }

    return (
        <div data-testid="success-tab">
            <div className="bg-white border border-lexiBorder rounded-2xl p-4 mb-6 flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-terracotta/12 flex items-center justify-center">
                    <Trophy className="w-4 h-4 text-terracotta" strokeWidth={1.75} />
                </div>
                <div className="flex-1 min-w-0">
                    <p className="text-[11px] tracking-[0.22em] uppercase text-inkSoft">Succès</p>
                    <p className="font-serif text-lg mt-0.5" data-testid="success-summary">
                        {totals.earned} / {totals.total} succès débloqués
                    </p>
                </div>
                <p className="hidden sm:block text-[11px] text-inkSoft italic max-w-[220px] text-right">
                    Les succès valorisent la constance, le rappel long terme et la parole.
                </p>
            </div>

            {CATEGORY_ORDER.filter((c) => (grouped[c] || []).length > 0).map((cat) => (
                <section key={cat} className="mb-8" data-testid={`success-cat-${cat}`}>
                    <p className="text-[11px] tracking-[0.22em] uppercase text-inkSoft mb-3">
                        {CATEGORY_LABELS[cat]}
                    </p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {grouped[cat].map((b) => (
                            <BadgeCard key={b.code} badge={b} />
                        ))}
                    </div>
                </section>
            ))}
        </div>
    );
}
